import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const STATUSES = ['Submitted', 'In Review', 'Approved', 'Rejected', 'Implemented'];

const STATUS_COLORS = {
  'Submitted':   { bg: '#e8f0fe', text: '#1a56db' },
  'In Review':   { bg: '#fff8e1', text: '#e65100' },
  'Approved':    { bg: '#e8f5e9', text: '#2e7d32' },
  'Rejected':    { bg: '#ffebee', text: '#c62828' },
  'Implemented': { bg: '#ede7f6', text: '#4527a0' },
};


const OWNER_EMAIL = 'c@cimonstarr.com';

function fmt(d) {
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function FeatureRequests({ userEmail }) {
  const [requests, setRequests] = useState([]);
  const [view, setView] = useState('list'); // 'list' | 'new' | 'detail'
  const [selected, setSelected] = useState(null);
  const [description, setDescription] = useState('');
  const [requestType, setRequestType] = useState('feature');
  const [generating, setGenerating] = useState(false);
  const [ownerNote, setOwnerNote] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const isOwner = (userEmail || '').toLowerCase() === OWNER_EMAIL;

  // Load from Supabase
  useEffect(() => { loadRequests(); }, []);

  async function loadRequests() {
    const { data, error } = await supabase
      .from('feature_requests')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error && data) {
      // Map snake_case to camelCase
      setRequests(data.map(r => ({
        id: r.id, type: r.type, description: r.description,
        spec: r.spec, status: r.status,
        submittedBy: r.submitted_by, ownerNote: r.owner_note,
        submittedAt: r.created_at, updatedAt: r.updated_at,
      })));
      // Migrate localStorage if needed
      try {
        const local = JSON.parse(localStorage.getItem('csid_feature_requests') || '[]');
        if (local.length > 0 && data.length === 0) {
          for (const req of local) {
            await supabase.from('feature_requests').insert({
              type: req.type || 'feature', description: req.description || '',
              spec: req.spec || null, status: req.status || 'Submitted',
              submitted_by: req.submittedBy || '', owner_note: req.ownerNote || '',
              created_at: req.submittedAt || new Date().toISOString(),
            });
          }
          localStorage.removeItem('csid_feature_requests');
          loadRequests();
        }
      } catch(e) {}
    }
  }

  async function save(updated) {
    setRequests(updated);
  }

  async function submitRequest() {
    if (!description.trim()) return;
    setGenerating(true);

    const isBug = requestType === 'bug';
    const sys = `You are a technical product manager for Cimon Starr Interior Design's procurement app.
The app is built with React + Vite, uses Supabase for data, and is deployed on Vercel.
It has these tabs: Items, Tracker, Proposals & POs, In/Out, Vendors, Ask, Billing (private).

${isBug ? `This is a BUG REPORT. Analyze what went wrong and respond with a JSON spec:
{
  "title": "short bug title",
  "affectedTabs": ["tab names"],
  "summary": "what is broken and how it affects the user",
  "userStory": "When I [action], I expect [result] but instead [what happens]",
  "acceptanceCriteria": ["the bug is fixed when...", "criterion 2"],
  "complexity": "low|medium|high",
  "complexityReason": "brief explanation of why this is easy or hard to fix",
  "implementationNotes": "likely cause and where in the code to look",
  "codeSketch": "pseudocode showing the fix or what to check"
}` : `This is a FEATURE REQUEST. Respond with a JSON spec:
{
  "title": "short feature name",
  "affectedTabs": ["tab names"],
  "summary": "2-3 sentence description of what this feature does and why it's useful",
  "userStory": "As a [role], I want to [action] so that [benefit]",
  "acceptanceCriteria": ["criterion 1", "criterion 2", "criterion 3"],
  "complexity": "low|medium|high",
  "complexityReason": "brief explanation",
  "implementationNotes": "technical notes about how this could be built — components affected, data needed, etc.",
  "codeSketch": "a brief pseudocode or component outline showing the key logic"
}`}`;

    try {
      const res = await fetch('/api/claude',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({system:sys,user:description,max_tokens:1200})});
      const data = await res.json();
      const text = data.text || '';
      let spec = null;
      try { spec = JSON.parse(text.replace(/```json|```/g, '').trim()); } catch {}

      const { data: inserted, error: insertError } = await supabase.from('feature_requests').insert({
        type: requestType, description, spec,
        status: 'Submitted',
        submitted_by: userEmail || 'Team member',
        owner_note: '',
        created_at: new Date().toISOString(),
      }).select().single();
      if (insertError) throw insertError;
      const newReq = {
        id: inserted.id, type: inserted.type, description: inserted.description,
        spec: inserted.spec, status: inserted.status,
        submittedBy: inserted.submitted_by, ownerNote: inserted.owner_note,
        submittedAt: inserted.created_at, updatedAt: inserted.updated_at,
      };
      setRequests(prev => [newReq, ...prev]);
      setDescription('');
      setView('detail');
      setSelected(newReq.id);
    } catch {
      alert('Could not generate spec. Please try again.');
    }
    setGenerating(false);
  }

  async function updateStatus(id, status) {
    await supabase.from('feature_requests').update({ status, updated_at: new Date().toISOString() }).eq('id', id);
    setRequests(prev => prev.map(r => r.id === id ? { ...r, status } : r));
    setSelected(null);
    setView('list');
  }

  async function saveOwnerNote(id) {
    await supabase.from('feature_requests').update({ owner_note: ownerNote, updated_at: new Date().toISOString() }).eq('id', id);
    setRequests(prev => prev.map(r => r.id === id ? { ...r, ownerNote } : r));
  }

  async function deleteRequest(id) {
    if (!window.confirm('Delete this request?')) return;
    await supabase.from('feature_requests').delete().eq('id', id);
    setRequests(prev => prev.filter(r => r.id !== id));
    setView('list');
    setSelected(null);
  }

  const inp = {
    width: '100%', padding: '8px 10px', border: '1px solid #ddd',
    borderRadius: 6, fontSize: 13, fontFamily: 'inherit',
    outline: 'none', background: '#fff', boxSizing: 'border-box',
  };

  // ── DETAIL VIEW ────────────────────────────────────────────────────────
  if (view === 'detail' && selected) {
    const req = requests.find(r => r.id === selected);
    if (!req) { setView('list'); return null; }
    const spec = req.spec;
    const sc = STATUS_COLORS[req.status] || STATUS_COLORS['Submitted'];

    return (
      <div style={{ maxWidth: 800 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <button onClick={() => setView('list')} style={{ background: 'none', border: 'none', color: '#1a56db', cursor: 'pointer', fontSize: 13 }}>← Back</button>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{spec?.title || (req.type === 'bug' ? 'Bug Report' : 'Feature Request')}</div>
          {req.type === 'bug' && <span style={{ background: '#fff8e1', color: '#e65100', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10 }}>BUG</span>}
          <span style={{ background: sc.bg, color: sc.text, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10 }}>{req.status}</span>
        </div>

        {/* Original request */}
        <div style={{ background: '#f5f5f3', border: '1px solid #e0e0e0', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#888', letterSpacing: 0.5, marginBottom: 6 }}>
            ORIGINAL REQUEST · {fmt(req.submittedAt)} · {req.submittedBy}
          </div>
          <div style={{ fontSize: 13, color: '#333', lineHeight: 1.6 }}>"{req.description}"</div>
        </div>

        {spec ? (
          <div>
            {/* Summary */}
            <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: '14px 16px', marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#888', letterSpacing: 0.5, marginBottom: 8 }}>SPEC</div>
              <div style={{ fontSize: 13, color: '#333', lineHeight: 1.6, marginBottom: 10 }}>{spec.summary}</div>
              <div style={{ fontSize: 12, color: '#555', fontStyle: 'italic', marginBottom: 10 }}>{spec.userStory}</div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {(spec.affectedTabs || []).map(t => (
                  <span key={t} style={{ background: '#e8f0fe', color: '#1a56db', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8 }}>{t}</span>
                ))}
                <span style={{ background: spec.complexity === 'low' ? '#e8f5e9' : spec.complexity === 'medium' ? '#fff8e1' : '#ffebee', color: spec.complexity === 'low' ? '#2e7d32' : spec.complexity === 'medium' ? '#e65100' : '#c62828', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8 }}>
                  {spec.complexity?.toUpperCase()} COMPLEXITY
                </span>
              </div>
            </div>

            {/* Acceptance criteria */}
            {spec.acceptanceCriteria?.length > 0 && (
              <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: '14px 16px', marginBottom: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#888', letterSpacing: 0.5, marginBottom: 8 }}>ACCEPTANCE CRITERIA</div>
                {spec.acceptanceCriteria.map((c, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, fontSize: 13, color: '#333' }}>
                    <span style={{ color: '#2e7d32', fontWeight: 700, flexShrink: 0 }}>✓</span>
                    <span>{c}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Implementation notes */}
            {spec.implementationNotes && (
              <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: '14px 16px', marginBottom: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#888', letterSpacing: 0.5, marginBottom: 8 }}>IMPLEMENTATION NOTES</div>
                <div style={{ fontSize: 12, color: '#444', lineHeight: 1.7 }}>{spec.implementationNotes}</div>
              </div>
            )}

            {/* Code sketch */}
            {spec.codeSketch && (
              <div style={{ background: '#1a1a1a', borderRadius: 8, padding: '14px 16px', marginBottom: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#888', letterSpacing: 0.5, marginBottom: 8 }}>CODE SKETCH</div>
                <pre style={{ fontSize: 11, color: '#e0e0e0', lineHeight: 1.6, whiteSpace: 'pre-wrap', fontFamily: 'var(--font-mono)', margin: 0 }}>{spec.codeSketch}</pre>
              </div>
            )}

            {/* Complexity reason */}
            {spec.complexityReason && (
              <div style={{ fontSize: 11, color: '#888', marginBottom: 16, fontStyle: 'italic' }}>
                Complexity note: {spec.complexityReason}
              </div>
            )}
          </div>
        ) : (
          <div style={{ color: '#888', fontSize: 13, marginBottom: 16 }}>Spec generation failed — the original request is saved above.</div>
        )}

        {/* Owner section */}
        {isOwner && (
          <div style={{ background: '#f5f0ff', border: '1px solid #d1c4e9', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#4527a0', letterSpacing: 0.5, marginBottom: 8 }}>OWNER REVIEW</div>
            <textarea
              value={ownerNote || req.ownerNote || ''}
              onChange={e => setOwnerNote(e.target.value)}
              placeholder="Add notes, questions, or modifications before approving..."
              rows={3}
              style={{ ...inp, resize: 'vertical', marginBottom: 10 }}
            />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {req.status !== 'Approved' && (
                <button onClick={() => { saveOwnerNote(req.id); updateStatus(req.id, 'Approved'); }}
                  style={{ padding: '7px 16px', background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  ✓ Approve
                </button>
              )}
              {req.status !== 'In Review' && (
                <button onClick={() => { saveOwnerNote(req.id); updateStatus(req.id, 'In Review'); }}
                  style={{ padding: '7px 16px', background: '#e65100', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Mark In Review
                </button>
              )}
              {req.status !== 'Implemented' && (
                <button onClick={() => updateStatus(req.id, 'Implemented')}
                  style={{ padding: '7px 16px', background: '#4527a0', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Mark Implemented
                </button>
              )}
              {req.status !== 'Rejected' && (
                <button onClick={() => { saveOwnerNote(req.id); updateStatus(req.id, 'Rejected'); }}
                  style={{ padding: '7px 14px', background: '#fff', color: '#c62828', border: '1px solid #ef9a9a', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Reject
                </button>
              )}
              <button onClick={() => { saveOwnerNote(req.id); alert('Notes saved.'); }}
                style={{ padding: '7px 14px', background: '#fff', color: '#555', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
                Save Notes
              </button>
              <button onClick={() => deleteRequest(req.id)}
                style={{ padding: '7px 14px', background: 'none', border: 'none', color: '#aaa', fontSize: 12, cursor: 'pointer', marginLeft: 'auto' }}>
                Delete
              </button>
            </div>
            {req.ownerNote && (
              <div style={{ marginTop: 10, padding: '8px 12px', background: '#fff', borderRadius: 6, fontSize: 12, color: '#4527a0', lineHeight: 1.6 }}>
                <strong>Saved note:</strong> {req.ownerNote}
              </div>
            )}
          </div>
        )}

        {/* Non-owner: show owner note if exists */}
        {!isOwner && req.ownerNote && (
          <div style={{ background: '#f5f0ff', border: '1px solid #d1c4e9', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#4527a0', letterSpacing: 0.5, marginBottom: 6 }}>REVIEW NOTE</div>
            <div style={{ fontSize: 13, color: '#333' }}>{req.ownerNote}</div>
          </div>
        )}
      </div>
    );
  }

  // ── NEW REQUEST FORM ────────────────────────────────────────────────────
  if (view === 'new') {
    return (
      <div style={{ maxWidth: 680 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <button onClick={() => setView('list')} style={{ background: 'none', border: 'none', color: '#1a56db', cursor: 'pointer', fontSize: 13 }}>← Back</button>
          <div style={{ fontWeight: 700, fontSize: 16 }}>New Feature Request</div>
        </div>

        {/* Type selector */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {[
            { value: 'feature', label: '✦ Feature Request', desc: 'New functionality or workflow improvement' },
            { value: 'bug', label: '⚠ Bug Report', desc: 'Something is broken or not working correctly' },
          ].map(t => (
            <button key={t.value} onClick={() => setRequestType(t.value)} style={{
              flex: 1, padding: '10px 16px', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
              border: requestType === t.value ? '2px solid #1a1a1a' : '1px solid #ddd',
              background: requestType === t.value ? '#1a1a1a' : '#fff',
              color: requestType === t.value ? '#fff' : '#333',
            }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{t.label}</div>
              <div style={{ fontSize: 11, opacity: 0.7 }}>{t.desc}</div>
            </button>
          ))}
        </div>

        <div style={{ background: requestType === 'bug' ? '#fff8e1' : '#e8f0fe', border: `1px solid ${requestType === 'bug' ? '#ffe082' : '#90caf9'}`, borderRadius: 8, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: requestType === 'bug' ? '#e65100' : '#0277bd', lineHeight: 1.6 }}>
          {requestType === 'bug'
            ? 'Describe what went wrong — what you did, what you expected, and what happened instead. Claude will generate a bug report with likely cause and fix.'
            : 'Describe what you need in plain English — no technical knowledge required. Claude will turn it into a full specification with implementation notes.'}
        </div>

        <div style={{ marginBottom: 8 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#555', letterSpacing: 0.3, display: 'block', marginBottom: 6 }}>
            DESCRIBE THE FEATURE OR WORKFLOW YOU NEED
          </label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder={'Examples:\n• "I want to see a list of all items that have been sitting at the same status for more than 2 weeks"\n• "When I mark an item as Received, automatically check if there\'s a final invoice due"\n• "A weekly summary email showing what arrived and what\'s overdue"'}
            rows={8}
            style={{ ...inp, resize: 'vertical', lineHeight: 1.6 }}
            autoFocus
          />
        </div>

        <div style={{ fontSize: 11, color: '#aaa', marginBottom: 16 }}>
          Be as specific as possible — mention which tab you're working in, what triggers the action, and what you want to see as a result.
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={submitRequest} disabled={!description.trim() || generating}
            style={{ padding: '9px 22px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: description.trim() && !generating ? 'pointer' : 'not-allowed', opacity: description.trim() && !generating ? 1 : 0.5 }}>
            {generating ? 'Generating spec...' : requestType === 'bug' ? 'Submit Bug Report →' : 'Submit Request →'}
          </button>
          <button onClick={() => setView('list')}
            style={{ padding: '9px 16px', background: 'none', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer', color: '#888' }}>
            Cancel
          </button>
        </div>

        {generating && (
          <div style={{ marginTop: 16, padding: '12px 16px', background: '#f5f5f3', borderRadius: 8, fontSize: 13, color: '#888' }}>
            Claude is analyzing your request and generating a specification...
          </div>
        )}
      </div>
    );
  }

  // ── LIST VIEW ───────────────────────────────────────────────────────────
  const statusCounts = STATUSES.reduce((acc, s) => {
    acc[s] = requests.filter(r => r.status === s).length;
    return acc;
  }, {});

  const filtered = filterStatus === 'All' ? requests : requests.filter(r => r.status === filterStatus);

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Requests & Bug Reports</div>
          <div style={{ fontSize: 13, color: '#666' }}>
            Submit a feature request, workflow improvement, or report a bug — Claude generates a full spec.
          </div>
        </div>
        <button onClick={() => setView('new')}
          style={{ padding: '8px 18px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          + New Request
        </button>
      </div>

      {/* Status filter pills */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {['All', ...STATUSES].map(s => {
          const cnt = s === 'All' ? requests.length : statusCounts[s];
          const sc = STATUS_COLORS[s];
          return (
            <button key={s} onClick={() => setFilterStatus(s)} style={{
              padding: '4px 12px', borderRadius: 14, border: '1px solid #ddd', fontSize: 11, fontWeight: 600, cursor: 'pointer',
              background: filterStatus === s ? '#1a1a1a' : (sc?.bg || '#f5f5f5'),
              color: filterStatus === s ? '#fff' : (sc?.text || '#555'),
            }}>
              {s} {cnt > 0 ? `(${cnt})` : ''}
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 24px', border: '1px dashed #ddd', borderRadius: 10, color: '#bbb' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>💡</div>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>No requests yet</div>
          <div style={{ fontSize: 13 }}>Click "+ New Request" to submit a feature request or report a bug</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(req => {
            const sc = STATUS_COLORS[req.status] || STATUS_COLORS['Submitted'];
            const spec = req.spec;
            return (
              <div key={req.id} onClick={() => { setSelected(req.id); setView('detail'); setOwnerNote(req.ownerNote || ''); }}
                style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: '14px 16px', cursor: 'pointer', background: '#fff', transition: 'border-color .15s' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, marginRight: 12 }}>
                    {req.type === 'bug' && <span style={{ background: '#fff8e1', color: '#e65100', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 6, flexShrink: 0 }}>BUG</span>}
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a' }}>
                      {spec?.title || req.description.slice(0, 60) + (req.description.length > 60 ? '...' : '')}
                    </div>
                  </div>
                  <span style={{ background: sc.bg, color: sc.text, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8, whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {req.status}
                  </span>
                </div>
                {spec?.summary && (
                  <div style={{ fontSize: 12, color: '#666', lineHeight: 1.5, marginBottom: 8 }}>{spec.summary}</div>
                )}
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  {(spec?.affectedTabs || []).slice(0, 3).map(t => (
                    <span key={t} style={{ background: '#e8f0fe', color: '#1a56db', fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 6 }}>{t}</span>
                  ))}
                  {spec?.complexity && (
                    <span style={{ fontSize: 9, fontWeight: 700, color: spec.complexity === 'low' ? '#2e7d32' : spec.complexity === 'medium' ? '#e65100' : '#c62828' }}>
                      {spec.complexity.toUpperCase()} COMPLEXITY
                    </span>
                  )}
                  <span style={{ fontSize: 10, color: '#aaa', marginLeft: 'auto' }}>
                    {req.submittedBy} · {fmt(req.submittedAt)}
                  </span>
                </div>
                {req.ownerNote && (
                  <div style={{ marginTop: 8, fontSize: 11, color: '#4527a0', fontStyle: 'italic' }}>
                    Note: {req.ownerNote}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
